import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import {
    mutateAdGroupCriteria,
    parseAdGroupCriteriaMutateResults,
    type IGoogleAdsAdGroupCriterionCreateOperation,
    type IGoogleAdsKeywordMutateResult,
} from "../services/google-ads-proxy-client";

const DEFAULT_KEYWORD_MATCH_TYPE = "BROAD";

export interface IUseGoogleAdsWriteKeywordsVariables {
    customerId: string;
    loginCustomerId?: string;
    /** Ad-group-level target only (SIM-104 v1) — e.g. `customers/123/adGroups/456`. */
    adGroupResourceName: string;
    keywords: string[];
    negative: boolean;
}

const buildAdGroupCriterionOperations = (
    adGroupResourceName: string,
    keywords: string[],
    negative: boolean,
): IGoogleAdsAdGroupCriterionCreateOperation[] =>
    keywords.map((keyword) => ({
        create: {
            adGroup: adGroupResourceName,
            negative,
            keyword: { text: keyword, matchType: DEFAULT_KEYWORD_MATCH_TYPE },
            // Google Ads rejects `status` on negative criteria in v24 — only enabled
            // (non-negative) creates set it.
            ...(negative ? {} : { status: "ENABLED" as const }),
        },
    }));

const writeGoogleAdsKeywords = async ({
    customerId,
    loginCustomerId,
    adGroupResourceName,
    keywords,
    negative,
}: IUseGoogleAdsWriteKeywordsVariables): Promise<IGoogleAdsKeywordMutateResult[]> => {
    const operations = buildAdGroupCriterionOperations(adGroupResourceName, keywords, negative);

    const response = await mutateAdGroupCriteria({
        customerId,
        loginCustomerId,
        operations,
    });

    return parseAdGroupCriteriaMutateResults(response, keywords);
};

/**
 * Creates one or more KEYWORD ad-group criteria in a single batched mutate — either enabled
 * ("Add keyword") or negative ("Add to negative keywords"), per SIM-111. Network/auth failures
 * (expired token, non-2xx from the proxy) reject the mutation and surface as this hook's own
 * `isError`/`error` state. Per-keyword outcomes (success / duplicate / error) are returned in
 * the resolved data instead, since a duplicate keyword is not a failed request — see
 * `parseAdGroupCriteriaMutateResults`.
 */
export const useGoogleAdsWriteKeywords = (): UseMutationResult<
    IGoogleAdsKeywordMutateResult[],
    Error,
    IUseGoogleAdsWriteKeywordsVariables
> => {
    return useMutation({
        mutationFn: writeGoogleAdsKeywords,
    });
};
